import { useEffect, useMemo, useRef, useState } from "react";

import { REQUIRED_MESSAGE, useDialogForm } from "@/ds";
import type {
  CategoriaProjecao,
  ClassificacaoEscolhida,
  CotacaoObra,
  MoedaOpcao,
  SimulacaoProjecao,
} from "@/modules/financeiro-obras";
import { useOpcoesProjecao } from "../../../../hooks/use-opcoes-projecao";
import type { OpcoesGravadas } from "../../../../hooks/use-opcoes-projecao";
import { useSimulacaoProjecao } from "../../../../hooks/use-simulacao-projecao";
import { formatMoeda, formatVariacao } from "../planejamento-controle.format";
import {
  CLASSIFICACAO_PENDENTE_TAG,
  CLASSIFICACAO_TITLE,
  CONVERSAO_TEXTO,
  CURVA_LABEL,
  CUSTO_PADRAO_APLICADO,
  DISTRIBUICAO_LABEL,
  EM_USO_TAG,
  FATOR_COM_IDADE,
  FATOR_SEM_IDADE,
  FATOR_TITLE,
  MATERIAIS_TITLE,
  SEM_CUSTO_PADRAO_TEXTO,
  SEM_CUSTO_PADRAO_TITLE,
  SERVICOS_TITLE,
  USAR_CUSTO_PADRAO_LABEL,
  VALOR_LABEL,
  VALOR_OBRIGATORIO,
  VARIACAO_CUSTO_PADRAO,
  VOLTAR_CONFIGURACAO_LABEL,
} from "./projecao-formulario.constants";
import { custoPadraoDaLeitura, textoSemCotacao } from "./projecao-formulario.custo-padrao";
import {
  SEM_EXTRAS,
  categoriaVazia,
  erroFator,
  formularioDaVigente,
  materiaisPreenchido,
  montarGravacao,
  montarSimulacao,
  podeSalvar,
  validar,
} from "./projecao-formulario.form";
import type {
  CategoriaForm,
  ErrosCategoria,
  ExtrasProjecao,
  ProjecaoForm,
} from "./projecao-formulario.form";
import type { CategoriaTabela } from "./projecao-coletores-tabela";
import type { CotacaoDaObra, ProjecaoFormularioProps } from "./types-projecao-formulario";

const AVISO_DISTRIBUICAO =
  "Selecione a distribuição por coletor de Serviços e Materiais para distribuir o valor nos coletores.";
const avisoCurva = (faltam: string[]) =>
  `Para ver a mensalização, selecione: ${faltam.join(", ")}.`;

type Chave = "servicos" | "materiais";

const VAZIO: ProjecaoForm & ExtrasProjecao = {
  servicos: categoriaVazia(null),
  materiais: categoriaVazia(null),
  ...SEM_EXTRAS,
};

interface PedidoCotacao {
  moeda: MoedaOpcao;
  chave: Chave | null;
  anterior: string | null;
}
const CATEGORIA: Record<Chave, CategoriaProjecao> = {
  servicos: "SERVICOS",
  materiais: "MATERIAIS",
};
const TITULO: Record<Chave, string> = { servicos: SERVICOS_TITLE, materiais: MATERIAIS_TITLE };

export function useProjecaoFormulario({
  obraId,
  alvo,
  obra,
  cotacaoDaObra,
  classificacaoEscopo,
  vigente = null,
  totalVigente,
  previaAposMudanca = false,
  ativo = true,
  mostrarObrigatorios = false,
}: ProjecaoFormularioProps) {
  const gravadas = useMemo<OpcoesGravadas | undefined>(
    () =>
      vigente
        ? {
            distribuicoes: {
              SERVICOS: vigente.servicos.distribuicao,
              MATERIAIS: vigente.materiais?.distribuicao,
            },
            curvas: { SERVICOS: vigente.servicos.curva, MATERIAIS: vigente.materiais?.curva },
          }
        : undefined,
    [vigente],
  );
  const consultaOpcoes = useOpcoesProjecao(gravadas, ativo);
  const { opcoes } = consultaOpcoes;
  const pronto = consultaOpcoes.status === "success";
  const moedaPadrao = opcoes.moedas.find((m) => m.nacional)?.id ?? null;
  const [pedidoCotacao, setPedidoCotacao] = useState<PedidoCotacao | null>(null);
  const [cotacoesDefinidas, setCotacoesDefinidas] = useState<readonly string[]>([]);
  const [recarga, setRecarga] = useState(0);
  const [cotacaoEditada, setCotacaoEditada] = useState<CotacaoDaObra | null>(null);

  const form = useDialogForm<ProjecaoForm & ExtrasProjecao>({
    initial: VAZIO,
    onSubmit: () => undefined,
  });

  const abertoComVigente = useRef(false);
  useEffect(() => {
    if (!vigente || !pronto || abertoComVigente.current) return;
    abertoComVigente.current = true;
    form.reset({
      ...formularioDaVigente(vigente, opcoes.moedas, moedaPadrao),
      usarCustoPadrao: vigente.usaCustoPadrao,
      aplicarFator: vigente.fatorIdade !== null,
      fatorIdade: vigente.fatorIdade,
      classificacao: null,
    });
  });

  const comMoeda = (c: CategoriaForm): CategoriaForm => ({
    ...c,
    moedaId: c.moedaId ?? moedaPadrao,
  });
  const valores: ProjecaoForm = {
    servicos: comMoeda(form.values.servicos),
    materiais: comMoeda(form.values.materiais),
  };

  const { usarCustoPadrao, aplicarFator, fatorIdade, classificacao } = form.values;
  const extras: ExtrasProjecao = { usarCustoPadrao, aplicarFator, fatorIdade, classificacao };

  const previa = useSimulacaoProjecao(
    obraId,
    previaAposMudanca && !form.isDirty ? null : montarSimulacao(alvo, valores, extras),
    undefined,
    recarga,
  );
  const simulacao: SimulacaoProjecao | null = previa.simulacao;
  const errosBase = validar(valores);
  const servicosObrigatorios: ErrosCategoria = mostrarObrigatorios
    ? {
        ...(valores.servicos.valor === null ? { valor: VALOR_OBRIGATORIO } : {}),
        ...(valores.servicos.distribuicaoId ? {} : { distribuicaoId: REQUIRED_MESSAGE }),
        ...(valores.servicos.curvaId ? {} : { curvaId: REQUIRED_MESSAGE }),
      }
    : {};
  const erros = {
    servicos: { ...servicosObrigatorios, ...errosBase.servicos },
    materiais: errosBase.materiais,
  };

  const alterar = <K extends keyof CategoriaForm>(
    chave: Chave,
    campo: K,
    valor: CategoriaForm[K],
  ) => form.setValue(chave, { ...form.values[chave], [campo]: valor });

  const semCotacao = (moeda: MoedaOpcao | undefined) =>
    !!moeda &&
    !moeda.nacional &&
    moeda.codigo !== cotacaoDaObra?.moeda &&
    !cotacoesDefinidas.includes(moeda.codigo);

  const escolherMoeda = (chave: Chave, id: string | null) => {
    const anterior = valores[chave].moedaId;
    alterar(chave, "moedaId", id);
    const moeda = opcoes.moedas.find((m) => m.id === id);
    if (moeda && semCotacao(moeda)) setPedidoCotacao({ moeda, chave, anterior });
  };

  const nomeDe = (lista: { id: string; nome: string }[], id: string | null) =>
    lista.find((o) => o.id === id)?.nome ?? null;

  const padrao = custoPadraoDaLeitura(vigente);
  const travadoPeloPadrao = (chave: Chave) => usarCustoPadrao && padrao?.[chave] != null;

  const categoriasEmUso: Chave[] = materiaisPreenchido(valores.materiais)
    ? ["servicos", "materiais"]
    : ["servicos"];
  const moedaDe = (chave: Chave) => opcoes.moedas.find((m) => m.id === valores[chave].moedaId);
  const pendenteDeCotacao = (chave: Chave) =>
    semCotacao(moedaDe(chave)) || !!simulacao?.[chave]?.pendenteCotacao;
  const chavePendente = categoriasEmUso.find(pendenteDeCotacao);
  const moedaPendente = chavePendente ? (moedaDe(chavePendente) ?? null) : null;

  const bloco = (chave: Chave, errosCategoria: ErrosCategoria) => {
    const categoria = CATEGORIA[chave];
    const atual = valores[chave];
    const resultado = simulacao?.[chave] ?? null;
    const moeda = opcoes.moedas.find((m) => m.id === atual.moedaId);
    const id = `projecao-${chave}`;
    return {
      ids: {
        cartao: id,
        valor: `${id}-valor`,
        moeda: `${id}-moeda`,
        distribuicao: `${id}-distribuicao`,
        distribuicaoErro: `${id}-distribuicao-error`,
        curva: `${id}-curva`,
        curvaErro: `${id}-curva-error`,
        pendente: `${id}-pendente`,
        conversao: `${id}-conversao`,
      },
      titulo: TITULO[chave],
      travado: travadoPeloPadrao(chave),
      valorLabel: VALOR_LABEL(TITULO[chave]),
      distribuicaoLabel: DISTRIBUICAO_LABEL(TITULO[chave]),
      curvaLabel: CURVA_LABEL(TITULO[chave]),
      valor: atual.valor,
      moedaId: atual.moedaId,
      prefixo: moeda?.simbolo,
      distribuicaoId: atual.distribuicaoId,
      curvaId: atual.curvaId,
      moedas: opcoes.moedas.map((m) => ({ label: m.codigo, value: m.id })),
      distribuicoes: opcoes.distribuicoes[categoria].map((o) => ({ label: o.nome, value: o.id })),
      curvas: opcoes.curvas[categoria].map((o) => ({ label: o.nome, value: o.id })),
      erros: errosCategoria,
      pendenteCotacao: categoriasEmUso.includes(chave) && pendenteDeCotacao(chave),
      conversao:
        resultado &&
        !resultado.pendenteCotacao &&
        moeda &&
        !moeda.nacional &&
        resultado.valorReais !== null
          ? CONVERSAO_TEXTO(formatMoeda(resultado.valorReais, "BRL"), moeda.codigo)
          : null,
      digitarValor: (v: number | null) => alterar(chave, "valor", v),
      escolherMoeda: (id: string | null) => escolherMoeda(chave, id),
      escolherDistribuicao: (id: string | null) => alterar(chave, "distribuicaoId", id),
      escolherCurva: (id: string | null) => alterar(chave, "curvaId", id),
    };
  };

  const faltaCurva = [
    ...(valores.servicos.curvaId ? [] : ["Curva de Serviços"]),
    ...(materiaisPreenchido(valores.materiais) && !valores.materiais.curvaId
      ? ["Curva de Materiais"]
      : []),
  ];
  const avisos = [
    ...(valores.servicos.distribuicaoId &&
    (!materiaisPreenchido(valores.materiais) || valores.materiais.distribuicaoId)
      ? []
      : [AVISO_DISTRIBUICAO]),
    ...(faltaCurva.length > 0 ? [avisoCurva(faltaCurva)] : []),
  ];

  const erroDoFator = erroFator(extras);
  const completo = podeSalvar(valores);
  const materiaisIncompleto =
    materiaisPreenchido(valores.materiais) && Object.keys(erros.materiais).length > 0;

  const [manuais, setManuais] = useState<Pick<ProjecaoForm, "servicos" | "materiais"> | null>(null);
  const idade = obra.idadeEmbarcacao ?? null;
  const moedaDoPadrao = (codigo: string) => opcoes.moedas.find((m) => m.codigo === codigo);

  const aplicarPadrao = () => {
    if (!padrao) return;
    setManuais({ servicos: form.values.servicos, materiais: form.values.materiais });
    const doPadrao = (atual: CategoriaForm, parte: { valor: number; moeda: string } | null) =>
      parte
        ? { ...atual, valor: parte.valor, moedaId: moedaDoPadrao(parte.moeda)?.id ?? atual.moedaId }
        : atual;
    form.setValues({
      ...form.values,
      servicos: doPadrao(form.values.servicos, padrao.servicos),
      materiais: doPadrao(form.values.materiais, padrao.materiais),
      usarCustoPadrao: true,
      aplicarFator: idade !== null,
      fatorIdade: idade,
    });
    const semCotacaoDoPadrao = [padrao.servicos, padrao.materiais]
      .map((parte) => (parte ? moedaDoPadrao(parte.moeda) : undefined))
      .find(semCotacao);
    if (semCotacaoDoPadrao) {
      setPedidoCotacao({ moeda: semCotacaoDoPadrao, chave: null, anterior: null });
    }
  };

  const voltarConfiguracaoOriginal = () => {
    form.setValues({
      ...form.values,
      ...(manuais ?? {}),
      usarCustoPadrao: false,
      aplicarFator: false,
      fatorIdade: null,
    });
    setManuais(null);
  };

  const embarcacao = obra.embarcacao;
  const variacaoPadrao = simulacao?.variacaoCustoPadrao ?? null;

  const distribuicoes: Record<CategoriaTabela, string | null> = {
    SERVICOS: nomeDe(opcoes.distribuicoes.SERVICOS, valores.servicos.distribuicaoId),
    MATERIAIS: nomeDe(opcoes.distribuicoes.MATERIAIS, valores.materiais.distribuicaoId),
  };

  return {
    corpo: montarGravacao(valores, extras),
    concluidoValido: pronto && completo && !erroDoFator,
    completo,
    materiaisIncompleto,
    cotacaoAlterada: cotacoesDefinidas.length > 0,
    isDirty: form.isDirty,
    consultaOpcoes: {
      status: consultaOpcoes.status,
      errorMessage: consultaOpcoes.errorMessage,
      recarregar: consultaOpcoes.recarregar,
    },
    cotacaoDaObra: cotacaoEditada ?? cotacaoDaObra,
    descartar: () => {
      form.reset(VAZIO);
      setManuais(null);
    },
    editarCotacao: (() => {
      const moeda = opcoes.moedas.find((m) => m.codigo === cotacaoDaObra?.moeda);
      return moeda && pronto
        ? () => setPedidoCotacao({ moeda, chave: null, anterior: null })
        : null;
    })(),
    servicos: bloco("servicos", erros.servicos),
    materiais: bloco("materiais", erros.materiais),
    custoPadrao: padrao
      ? {
          titulo: USAR_CUSTO_PADRAO_LABEL,
          usando: usarCustoPadrao,
          valores: [
            ...[
              ["Serviços", padrao.servicos],
              ["Materiais", padrao.materiais],
            ]
              .filter((par): par is [string, { valor: number; moeda: string }] => par[1] !== null)
              .map(([nome, p]) => `${nome}: ${formatMoeda(p.valor, p.moeda)} (${p.moeda})`),
            ...padrao.semCotacao.map(textoSemCotacao),
          ].join(" · "),
          aplicado: usarCustoPadrao ? CUSTO_PADRAO_APLICADO(padrao.ano) : null,
          tag: usarCustoPadrao ? EM_USO_TAG : null,
          variacao:
            usarCustoPadrao && variacaoPadrao !== null
              ? VARIACAO_CUSTO_PADRAO(formatVariacao(variacaoPadrao))
              : null,
          voltarLabel: VOLTAR_CONFIGURACAO_LABEL,
          desabilitado: !pronto,
          alternar: (marcado: boolean) =>
            marcado ? aplicarPadrao() : voltarConfiguracaoOriginal(),
          voltar: voltarConfiguracaoOriginal,
        }
      : null,
    semCustoPadrao: padrao
      ? null
      : { titulo: SEM_CUSTO_PADRAO_TITLE, texto: SEM_CUSTO_PADRAO_TEXTO },
    fator:
      padrao && usarCustoPadrao
        ? {
            titulo: FATOR_TITLE,
            texto:
              idade !== null ? FATOR_COM_IDADE(embarcacao, idade) : FATOR_SEM_IDADE(embarcacao),
            valor: fatorIdade,
            aplicar: aplicarFator,
            erro: erroDoFator ?? null,
            digitar: (valor: number | null) => form.setValue("fatorIdade", valor),
            alternarAplicar: (ligado: boolean) => form.setValue("aplicarFator", ligado),
          }
        : null,
    classificacao:
      classificacaoEscopo && classificacaoEscopo !== "NAO_SE_APLICA"
        ? {
            titulo: CLASSIFICACAO_TITLE,
            valor:
              classificacao ?? (classificacaoEscopo === "PENDENTE" ? null : classificacaoEscopo),
            opcoes: [
              { label: "CAPEX", value: "CAPEX" },
              { label: "OPEX", value: "OPEX" },
            ],
            pendente:
              classificacaoEscopo === "PENDENTE" && !classificacao
                ? CLASSIFICACAO_PENDENTE_TAG
                : null,
            escolher: (valor: ClassificacaoEscolhida | null) =>
              form.setValue("classificacao", valor),
          }
        : null,
    totalEscopo: formatMoeda(simulacao?.total ?? totalVigente ?? 0, "BRL"),
    tabela: {
      simulacao,
      distribuicoes,
      avisos,
      erro: previa.estado === "erro" ? previa.errorMessage : null,
      calculando: previa.estado === "carregando",
    },
    moedaPendente: moedaPendente?.codigo ?? null,
    definirCotacaoPendente: () => {
      if (moedaPendente) setPedidoCotacao({ moeda: moedaPendente, chave: null, anterior: null });
    },
    cotacaoDialog: pedidoCotacao
      ? {
          obraId,
          moeda: { id: pedidoCotacao.moeda.id, codigo: pedidoCotacao.moeda.codigo },
          onClose: () => {
            if (pedidoCotacao.chave)
              alterar(pedidoCotacao.chave, "moedaId", pedidoCotacao.anterior);
            setPedidoCotacao(null);
          },
          onDefinida: (cotacao: CotacaoObra) => {
            setCotacoesDefinidas((atual) => [...atual, cotacao.moeda.codigo]);
            if (cotacao.moeda.codigo === cotacaoDaObra?.moeda)
              setCotacaoEditada({
                moeda: cotacao.moeda.codigo,
                valor: formatMoeda(cotacao.cotacao, "BRL").replace("R$ ", ""),
              });
            setRecarga((atual) => atual + 1);
            setPedidoCotacao(null);
          },
        }
      : null,
  };
}
